import React, { useState } from "react";


export function toNum(val) {
  if (!val) return 0;
  if (typeof val === "number") return val;
  return Number(String(val).replace(/[^0-9.-]+/g, ""));
}

export const frequencyToMonthly = (amount, frequency, hoursPerWeek = 40) => {
  const num = parseFloat(amount);
  const hrs = parseFloat(hoursPerWeek) || 0;

  if (isNaN(num)) return "";

  switch (frequency) {
    case "Hourly":
      return hrs > 0 ? (num * hrs * 52) / 12 : 0;
    case "Weekly":
      return (num * 52) / 12;
    case "Bi-Weekly":
      return (num * 26) / 12;
    case "Semi-Monthly":
      return num * 2;
    case "Monthly":
      return num;
    case "Annually":
      return num / 12;
    default:
      return 0;
  }
};

export const formatCurrency = (value) => {
  if (value === undefined || value === null) return "$0.00";
  const num = parseFloat(String(value).replace(/[^0-9.]/g, ""));
  if (isNaN(num)) return "$0.00";
  return num.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export const capitalizeName = (value) => {
  return value
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase())
    .replace(/\s+/g, " ")
    .trim();
};

export const formatPhoneNumber = (value = "") => {
  const cleaned = value.replace(/\D/g, "");
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(
      6
    )}`;
  }
  return value;
};

export function AmortizationCalculator() {
  // State for the form
  const [price, setPrice] = useState(200000);
  const [rate, setRate] = useState(6.25);
  const [down, setDown] = useState(3.5);
  const [years, setYears] = useState(30);
  const [months, setMonths] = useState(0);
  const [sendPDF, setSendPDF] = useState(false);
  const [showAllRows, setShowAllRows] = useState(false);

  // Calculate values
  const dpAmt = price * (down / 100);
  const loanAmt = price - dpAmt;
  const n = years * 12 + months;
  const r = rate / 100 / 12;
  const monthly =
    r === 0 ? loanAmt / n : (loanAmt * r) / (1 - Math.pow(1 + r, -n));
  const totalPayments = n;

  const amortTable = [];
  let balance = loanAmt,
    totalInterest = 0;
  for (let i = 1; i <= n; ++i) {
    const interest = balance * r;
    const principal = monthly - interest;
    balance -= principal;
    totalInterest += interest;
    amortTable.push({
      payment: i,
      paymentAmt: monthly,
      principal,
      interest,
      totalInterest,
      balance: Math.max(balance, 0),
    });
  }
  const tableSummary =
    amortTable.length > 24
      ? [...amortTable.slice(0, 12), ...amortTable.slice(-12)]
      : amortTable;

  return (
    <div style={{ minWidth: 300, maxWidth: 500 }}>
      <div
        style={{
          background: "#f4f4f4",
          borderRadius: "7px",
          marginBottom: "1.1em",
          padding: "0.6em 1.1em",
        }}
      >
        <span style={{ fontSize: "1.5em", fontWeight: 400 }}>
          Amortization Calculator
        </span>

        {/* Price */}
        <div style={{ margin: "1.1em 0 0.2em 0", fontSize: "1.12em" }}>
          Price of Home
        </div>
        <div style={{ fontSize: "2em", fontWeight: 600, marginBottom: 0 }}>
          {price.toLocaleString("en-US", {
            style: "currency",
            currency: "USD",
          })}
        </div>
        <input
          type="range"
          min={10000}
          max={900000}
          step={1000}
          value={price}
          onChange={(e) => setPrice(Number(e.target.value))}
          style={{ width: "100%" }}
        />

        {/* Rate */}
        <div style={{ marginTop: "1.15em", fontSize: "1.08em" }}>
          Interest Rate (%)
        </div>
        <div style={{ fontSize: "2em", fontWeight: 600 }}>
          {rate.toFixed(2)}
        </div>
        <input
          type="range"
          min={2}
          max={15}
          step={0.01}
          value={rate}
          onChange={(e) => setRate(Number(e.target.value))}
          style={{ width: "100%" }}
        />

        {/* Down */}
        <div style={{ marginTop: "1.15em", fontSize: "1.08em" }}>
          Down Payment (%)
        </div>
        <div style={{ fontSize: "2em", fontWeight: 600 }}>{down}</div>
        <input
          type="range"
          min={0}
          max={80}
          step={0.1}
          value={down}
          onChange={(e) => setDown(Number(e.target.value))}
          style={{ width: "100%" }}
        />

        {/* Term */}
        <div style={{ marginTop: "1.15em", fontSize: "1.08em" }}>Term</div>
        <label style={{ marginRight: 16 }}>
          <input
            type="radio"
            checked={months === 0}
            onChange={() => setMonths(0)}
          />
          Years
        </label>
        <label>
          <input
            type="radio"
            checked={months !== 0}
            onChange={() => setMonths(12)}
          />
          Months
        </label>
        <input
          type="number"
          min={months === 0 ? 1 : 0}
          max={months === 0 ? 40 : 360}
          value={months === 0 ? years : months}
          onChange={(e) =>
            months === 0
              ? setYears(Number(e.target.value))
              : setMonths(Number(e.target.value))
          }
          style={{ marginLeft: 10, width: 64 }}
        />

        {/* Submit/Reset */}
        <div style={{ marginTop: "1.1em", display: "flex", gap: 16 }}>
          <button
            type="button"
            style={{
              background: "#aaa",
              color: "#fff",
              fontWeight: 600,
              borderRadius: 4,
              padding: "0.6em 2.1em",
              fontSize: "1.1em",
              border: "none",
              cursor: "pointer",
            }}
            onClick={() => {
              setPrice(225000);
              setRate(6.25);
              setDown(3.5);
              setYears(30);
              setMonths(0);
            }}
          >
            RESET
          </button>
        </div>
      </div>

      {/* Loan Details Summary */}
      <div
        style={{
          background: "#ededed",
          borderRadius: 7,
          marginBottom: "1.5em",
          padding: "1.1em",
        }}
      >
        <div
          style={{ fontSize: "1.35em", fontWeight: 400, marginBottom: "0.8em" }}
        >
          Loan Details
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", marginBottom: 10 }}>
          <div style={{ flex: "1 0 210px" }}>
            <b>Original Loan Amount:</b> <br />
            {price.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            })}
          </div>
          <div style={{ flex: "1 0 110px" }}>
            <b>Down Payment:</b> <br />
            <span style={{ fontWeight: 700 }}>{down} %</span>
          </div>
          <div style={{ flex: "1 0 110px" }}>
            <b>Interest:</b> <br />
            <span style={{ fontWeight: 700 }}>{rate} %</span>
          </div>
          <div style={{ flex: "1 0 160px" }}>
            <b>Term:</b> <br />
            <span style={{ fontWeight: 700 }}>
              {months === 0 ? years + " Years" : months + " Months"}
            </span>
          </div>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", marginBottom: 14 }}>
          <div style={{ flex: "1 0 210px" }}>
            <b>Loan After Down Payment:</b> <br />
            {loanAmt.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            })}
          </div>
          <div style={{ flex: "1 0 110px" }}>
            <b>Down Payment Amount:</b> <br />
            {dpAmt.toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            })}
          </div>
          <div style={{ flex: "1 0 180px" }}>
            <b>Monthly Payment (P & I):</b> <br />
            <span style={{ fontWeight: 700 }}>
              {monthly.toLocaleString("en-US", {
                style: "currency",
                currency: "USD",
              })}
            </span>
          </div>
          <div style={{ flex: "1 0 160px" }}>
            <b>Total Payments:</b> <br />
            <span style={{ fontWeight: 700 }}>{totalPayments}</span>
          </div>
        </div>
      </div>

      {/* Amortization Schedule Table */}
      <div
        style={{
          background: "#ededed",
          borderRadius: 7,
          padding: "1.1em",
          overflowX: "auto",
        }}
      >
        <div
          style={{ fontSize: "1.18em", fontWeight: 500, marginBottom: "0.7em" }}
        >
          Amortization Schedule (P & I)
        </div>
        <table
          style={{
            width: "100%",
            fontSize: "0.99em",
            background: "transparent",
          }}
        >
          <thead>
            <tr>
              <th>Payment</th>
              <th>Payment Amt</th>
              <th>Principal</th>
              <th>Interest</th>
              <th>Total Interest</th>
              <th>Balance</th>
            </tr>
          </thead>
          <tbody>
            {(showAllRows ? amortTable : tableSummary).map((row, idx) => (
              <tr key={row.payment + "-" + idx}>
                <td>{row.payment}</td>
                <td>
                  {row.paymentAmt.toLocaleString("en-US", {
                    style: "currency",
                    currency: "USD",
                  })}
                </td>
                <td>
                  {row.principal.toLocaleString("en-US", {
                    style: "currency",
                    currency: "USD",
                  })}
                </td>
                <td>
                  {row.interest.toLocaleString("en-US", {
                    style: "currency",
                    currency: "USD",
                  })}
                </td>
                <td>
                  {row.totalInterest.toLocaleString("en-US", {
                    style: "currency",
                    currency: "USD",
                  })}
                </td>
                <td>
                  <b>
                    {row.balance.toLocaleString("en-US", {
                      style: "currency",
                      currency: "USD",
                    })}
                  </b>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ color: "#555", fontSize: "0.98em", marginTop: "0.5em" }}>
          {!showAllRows && amortTable.length > 24 && (
            <>
              <i>Showing first 12 and last 12 payments. </i>
              <button
                type="button"
                style={{
                  background: "#0096b2",
                  color: "#fff",
                  fontWeight: 600,
                  borderRadius: 5,
                  border: "none",
                  padding: "0.28em 1em",
                  marginLeft: 10,
                  cursor: "pointer",
                }}
                onClick={() => setShowAllRows(true)}
              >
                Show All Payments
              </button>
            </>
          )}
          {showAllRows && amortTable.length > 24 && (
            <button
              type="button"
              style={{
                background: "#ededed",
                color: "#007b94",
                fontWeight: 600,
                borderRadius: 5,
                border: "1px solid #cce7e4",
                padding: "0.25em 1em",
                marginLeft: 10,
                cursor: "pointer",
              }}
              onClick={() => setShowAllRows(false)}
            >
              Show Summary Only
            </button>
          )}
        </div>
      </div>

      {/* Disclaimer */}
      <div
        style={{
          marginTop: "1.5em",
          padding: "1em",
          background: "#f9eec9",
          borderRadius: 7,
          color: "#7a5203",
          fontWeight: 500,
          fontSize: "1.06em",
          textAlign: "justify",
        }}
      >
        <b>Disclaimer:</b> This calculator is for informational purposes only
        and does not constitute a Loan Estimate or credit decision. For an
        official Loan Estimate or to apply for a loan, please contact a licensed
        loan officer. MLO 660 NMLS 1718857 Home Mortgage Bankers NMLS-157164.
        Equal Housing Opportunity lender.
      </div>
    </div>
  );
}

export function getAllFormSummaryFields(clientData) {
  const HIDDEN_KEYS = [
    "firstName",
    "lastName",
    "email",
    "phone",
    "maritalStatus",
    "location",
    "income",
    "employmentType",
    "creditScore",
    "spouseFirstName",
    "spouseLastName",
    "spouseEmail",
    "spousePhone",
    "spouseIncome",
    "spouseEmploymentType",
    "spouseCreditScore",
    "incomeFrequency",
    "spouseFrequency",
    // ...add more fields as needed
  ];

  const d =
    clientData?.formDataPurchase || clientData?.formDataRefinance || clientData;
  if (!d) return [];
  return Object.entries(d)
    .filter(([key]) => !/password/i.test(key) && !HIDDEN_KEYS.includes(key))
    .map(([key, value]) => [
      key.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase()),
      typeof value === "object" && value !== null
        ? JSON.stringify(value, null, 2)
        : String(value),
    ]);
}

export function isStepComplete(step, formDataPurchase) {
  switch (step) {
    case 1:
      return !!formDataPurchase.location;
    case 2:
      return !!formDataPurchase.income && !!formDataPurchase.incomeFrequency;
    case 3:
      return !!formDataPurchase.maritalStatus;
    case 4:
      return formDataPurchase.maritalStatus === "Married"
        ? !!formDataPurchase.prenuptialAgreement
        : true;
    case 5:
      if (
        formDataPurchase.maritalStatus === "Married" &&
        formDataPurchase.prenuptialAgreement === "No"
      ) {
        return (
          !!formDataPurchase.spouseFirstName &&
          !!formDataPurchase.spouseLastName &&
          !!formDataPurchase.spousePhone &&
          !!formDataPurchase.spouseEmail
        );
      }
      return true;
    case 6:
      return (
        !!formDataPurchase.spouseIncome && !!formDataPurchase.spouseFrequency
      );
    case 7:
      return !!formDataPurchase.employmentType;
    case 8:
      return !!formDataPurchase.workExperienceDuration;
    case 9:
      return formDataPurchase.workExperienceDuration === "<2"
        ? !!formDataPurchase.workExperienceDuration // (or another field here)
        : true;
    case 10:
      return formDataPurchase.workExperienceDuration === "<2"
        ? !!formDataPurchase.occupation2Years
        : true;
    case 11:
      return formDataPurchase.workExperienceDuration === "No"
        ? !!formDataPurchase.studentStatus
        : true;
    case 12:
      return formDataPurchase.maritalStatus === "Married"
        ? !!formDataPurchase.spouseEmploymentType
        : true;
    case 13:
      return formDataPurchase.maritalStatus === "Married"
        ? !!formDataPurchase.spouseWorkExperienceDuration
        : true;
    case 14: {
          return !!formDataPurchase.hasDebts;

  const toNum = (v) =>
    parseFloat((v || "").toString().replace(/[^0-9.]/g, "")) || 0;

  const debtFieldsMap = {
    autoLoan: "autoLoan",
    personalLoan: "personalLoan",
    studentLoan: "studentLoanMonthly",
    creditCards: "creditCard1",
    storeCards: "storeCards",
    furnitureLoans: "furnitureLoans",
    otherDebts: "otherDebts",
  };
  return formDataPurchase.selectedDebts.every((debt) => {
    if (debt === "creditCards") {
      return (
        toNum(formDataPurchase.creditCard1) ||
        toNum(formDataPurchase.creditCard2) ||
        toNum(formDataPurchase.creditCard3) ||
        toNum(formDataPurchase.creditCard4)
      );
    }
    if (debt === "studentLoan") {
      return (
        toNum(formDataPurchase.studentLoanMonthly) ||
        toNum(formDataPurchase.studentLoanTotal)
      );
    }
    return toNum(formDataPurchase[debtFieldsMap[debt]]);
  });
}
    case 15:
      if (
        formDataPurchase.maritalStatus === "Married" &&
        formDataPurchase.prenuptialAgreement !== "Yes"
      ) {
        return (
          !!formDataPurchase.creditScore && !!formDataPurchase.spouseCreditScore
        );
      }
      return !!formDataPurchase.creditScore;
    case 16:
      return !!formDataPurchase.bankruptcyOrForeclosure;
    case 17:
      return !!formDataPurchase.veteranStatus;
    case 18:
      return !!formDataPurchase.stateTaxesFiled;
    case 19:
      if (formDataPurchase.hasPropertyToPurchase === "Yes") {
        return (
          !!formDataPurchase.salesPrice &&
          !!formDataPurchase.propertyStreet &&
          !!formDataPurchase.propertyCounty &&
          !!formDataPurchase.propertyZip
        );
      }
      return true;

    default:
      return true;
  }
}

export function stepLabel(step) {
  switch (step) {
    case 1:
      return "Location";
    case 2:
      return "Client Income & Frequency";
    case 3:
      return "Marital Status";
    case 4:
      return "Prenuptial Agreement";
    case 5:
      return "Spouse Information";
    case 6:
      return "Spouse Income";
    case 7:
      return "Employment Type";
    case 8:
      return "Work Experience Duration";
    case 9:
      return "Work Experience Duration Details";
    case 10:
      return "Occupation for 2+ Years";
    case 11:
      return "Student Status";
    case 12:
      return "Spouse Employment Type";
    case 13:
      return "Spouse Work Experience Duration";
    case 14:
      return "Monthly Debts";
    case 15:
      return "Credit Score";
    case 16:
      return "Bankruptcy or Foreclosure";
    case 17:
      return "Veteran Status";
    case 18:
      return "State Taxes Filed";
    case 19:
      return "Property to Purchase";
    // Add more cases as needed for additional steps
    default:
      return `Step ${step}`;
  }
}

export function getExcludedSteps(formData) {
  const excluded = [];

  // Spouse-related steps
  if (formData.maritalStatus !== "Married") {
    excluded.push(4, 5, 6, 12, 13);
  }

  if (formData.prenuptialAgreement === "Yes") {
    excluded.push(5, 6, 12, 13);
  }
  // Retired path skips work experience steps
  if (formData.employmentType === "Retired") {
    excluded.push(8, 9, 10, 11);
    if (formData.prenuptialAgreement == "Yes") {
      excluded.push(13);
    }
  }

  if (formData.workExperienceDuration === ">2") {
    excluded.push(9, 10, 11);
  }

  return excluded;
}
